import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as usersApi from '../api/users';
import { ErrorAlert, Spinner } from '../components/ui';
import { ui, cn } from '../lib/ui';

/**
 * Landing page for the secondary-email verification link:
 * /verify-secondary-email?userId=...&email=...&token=...
 *
 * This page did not exist until 2026-09-04, while handleAddSecondaryEmail was
 * already mailing links to it — so adding a secondary email sent a link that
 * went nowhere, and the address could never be confirmed. The API route it
 * calls was missing too.
 *
 * All three parameters are required. userId is in the request path because the
 * handler resolves the account with findUserById; there is no index on
 * secondaryEmails, so the address alone cannot find it. No session is needed —
 * the token delivered to that inbox is the proof of ownership.
 */
export default function VerifySecondaryEmail() {
  const [params] = useSearchParams();
  const userId = params.get('userId');
  const email = params.get('email');
  const token = params.get('token');

  const complete = Boolean(userId && email && token);
  const [state, setState] = useState(complete ? 'verifying' : 'missing');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!complete) return;
    let cancelled = false;
    usersApi
      .verifySecondaryEmail(userId, email, token)
      .then(() => !cancelled && setState('verified'))
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setState('failed');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [complete, userId, email, token]);

  return (
    <div className={ui.authScreen}>
      <div className={ui.authCard}>
        <h1 className={ui.h1}>Secondary email verification</h1>

        {state === 'missing' && (
          <p>
            This link is incomplete. Use the full link from your verification email, or add
            the address again from your profile to get a new one.
          </p>
        )}

        {state === 'verifying' && <Spinner label="Verifying this address…" />}

        {state === 'verified' && (
          <>
            <div className={ui.alertSuccess}>
              {email} is verified and added to your account.
            </div>
            <Link className={ui.btnPrimary} to="/profile">
              Back to your profile
            </Link>
          </>
        )}

        {state === 'failed' && (
          <>
            <ErrorAlert error={error} />
            <p className={cn(ui.muted, 'text-body-s')}>
              Verification links expire. Add the address again from your profile to get a
              new one.
            </p>
            <Link className={ui.btnSecondary} to="/profile">
              Back to your profile
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
